import { z } from 'zod';
/**
 * Languages available for the alternative-language configuration feature.
 * To add a new language, append an entry here — no schema or migration changes needed.
 */
export const SUPPORTED_ALTERNATIVE_LANGUAGES = [
    { label: 'English', locale: 'en_Latn' },
    { label: 'Traditional Chinese', locale: 'zh_Hant' },
    { label: 'Simplified Chinese', locale: 'zh_Hans' },
];
/**
 * Resolves a locale key (e.g. "zh_Hant") to its display label ("Traditional Chinese").
 * Falls back to the raw locale key when the locale is not in the supported list.
 */
export const getLocaleLabel = (localeKey) => SUPPORTED_ALTERNATIVE_LANGUAGES.find((l) => l.locale === localeKey)?.label ?? localeKey;
/**
 * Fields on a ClientAccount that support an alternative-language value.
 */
export const ALTERNATIVE_LANGUAGE_CLIENT_ACCOUNT_FIELDS = [
    { key: 'legalName', label: 'Legal Name' },
    { key: 'tradingName', label: 'Trading Name' },
    { key: 'correspondenceAddress', label: 'Correspondence Address' },
    { key: 'description', label: 'Description' },
];
/**
 * Fields on a Contact that support an alternative-language value.
 */
export const ALTERNATIVE_LANGUAGE_CONTACT_FIELDS = [
    { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'fullName', label: 'Full Name' },
];
export const DEFAULT_ALTERNATIVE_LANGUAGE_ENABLED_FIELDS = {
    legalName: true,
    tradingName: true,
    correspondenceAddress: true,
    description: true,
    firstName: true,
    middleName: true,
    lastName: true,
    fullName: true,
};
/**
 * Name fields whose alternative-language value may be accepted in place of the
 * primary value (e.g. Hong Kong accepts a Chinese OR English legal name).
 */
export const ALTERNATIVE_LANGUAGE_IN_LIEU_OF_PRIMARY_FIELDS = [
    { key: 'legalName', label: 'Legal Name' },
    { key: 'tradingName', label: 'Trading Name' },
];
export const DEFAULT_ALTERNATIVE_LANGUAGE_IN_LIEU_OF_PRIMARY = {
    legalName: false,
    tradingName: false,
};
const buildOptionalBoolShape = (keys) => {
    const shape = {};
    for (const key of keys) {
        shape[key] = z.boolean().optional();
    }
    return shape;
};
const ENABLED_FIELD_KEYS = [
    ...ALTERNATIVE_LANGUAGE_CLIENT_ACCOUNT_FIELDS.map((f) => f.key),
    ...ALTERNATIVE_LANGUAGE_CONTACT_FIELDS.map((f) => f.key),
];
const IN_LIEU_OF_PRIMARY_KEYS = ALTERNATIVE_LANGUAGE_IN_LIEU_OF_PRIMARY_FIELDS.map((f) => f.key);
export const PerLocaleAlternativeLanguageConfigSchema = z.object({
    enabledFields: z.object(buildOptionalBoolShape(ENABLED_FIELD_KEYS)).optional(),
    inLieuOfPrimary: z.object(buildOptionalBoolShape(IN_LIEU_OF_PRIMARY_KEYS)).optional(),
});
export const SetAlternativeLanguageBodySchema = z.object({
    enabled: z.boolean().optional(),
    locales: z.record(z.string(), PerLocaleAlternativeLanguageConfigSchema).optional(),
});
//# sourceMappingURL=AlternativeLanguage.js.map